import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Table, Alert } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import { useAccounts } from 'api/hooks/use-accounts';
import useMunicipalitySettings from 'api/hooks/use-municipality-settings';
import TableFooter from 'shared/tables/table-footer';
import TableLoading from 'shared/tables/table-loading';
import FilterableHeader from 'shared/column-filters/FilterableHeader';
import { ACCOUNT_TYPE_OPTIONS, COLUMN_DEFINITIONS } from './constants';
import { SEGMENT_TYPES, getSegmentName as getSegmentNameHelper } from './helpers';
import AccountDetailModal from './account-detail-modal';

// Filter param keys that should be synced to URL
// Note: Operators are omitted since all text filters use "contains"
// Filter columns use _search suffix to match API param naming convention
const FILTER_PARAM_KEYS = [
  'page', 'perPage',
  'full_account_search',
  'account_type_search',
  'account_description_search',
  ...SEGMENT_TYPES.map(seg => `${seg}_search`)
];

// Parse filter params from URL search params
const parseParamsFromUrl = (searchParams) => {
  const params = {
    page: 1,
    perPage: 25
  };

  FILTER_PARAM_KEYS.forEach(key => {
    const value = searchParams.get(key);
    if (value !== null && value !== '') {
      if (key === 'page' || key === 'perPage') {
        params[key] = parseInt(value, 10) || params[key];
      } else if (key === 'account_type_search') {
        // Multiselect values are comma-separated
        params[key] = value.split(',');
      } else {
        params[key] = value;
      }
    }
  });

  return params;
};

const AccountsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [params, setParamsState] = useState(() => parseParamsFromUrl(searchParams));
  const [activePopoverId, setActivePopoverId] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  // Sync params state to URL when params change
  useEffect(() => {
    const newSearchParams = new URLSearchParams();

    // Preserve existing non-filter params from current URL (use window.location to avoid dependency cycle)
    const currentSearchParams = new URLSearchParams(window.location.search);
    currentSearchParams.forEach((value, key) => {
      if (!FILTER_PARAM_KEYS.includes(key)) {
        newSearchParams.set(key, value);
      }
    });

    // Add filter params that have values
    Object.entries(params).forEach(([key, value]) => {
      if (FILTER_PARAM_KEYS.includes(key) && value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value) && value.length > 0) {
          newSearchParams.set(key, value.join(','));
        } else if (!Array.isArray(value)) {
          // Don't include default values in URL
          if (key === 'page' && value === 1) return;
          if (key === 'perPage' && value === 25) return;
          newSearchParams.set(key, value);
        }
      }
    });

    setSearchParams(newSearchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  // Simple state updater - supports both direct objects and functional updates
  const setParams = useCallback((newParams) => {
    setParamsState(prevParams => {
      return typeof newParams === 'function' ? newParams(prevParams) : newParams;
    });
  }, []);

  const {
    isLoading,
    isSuccess,
    isError,
    error,
    data: accountsData
  } = useAccounts({ params, options: { keepPreviousData: true } });

  const { data: settings, isSuccess: settingsLoaded } = useMunicipalitySettings({ options: {} });

  const getSegmentName = (accessType) => getSegmentNameHelper(settings, settingsLoaded, accessType);

  // Determine which segments are present across all accounts
  const activeSegments = useMemo(() => {
    if (!isSuccess || !accountsData?.data) return [];

    const segmentsWithData = new Set();
    accountsData.data.forEach((account) => {
      SEGMENT_TYPES.forEach((segType) => {
        if (account[segType]?.code) {
          segmentsWithData.add(segType);
        }
      });
    });

    // Return segments in the correct order
    return SEGMENT_TYPES.filter((seg) => segmentsWithData.has(seg));
  }, [isSuccess, accountsData]);

  // Create column definitions for segment columns
  // Note: accessor_key uses _search suffix to match API param naming convention
  const getSegmentColumnDef = (segType) => ({
    accessor_key: `${segType}_search`,
    database_column_name: segType,
    display_name: getSegmentName(segType),
    filter_type: 'basic',
    is_sortable: false
  });

  const totalColumns = 3 + activeSegments.length;

  return (
    <>
      {isError && (
        <Alert variant="danger">
          Error loading accounts: {error?.message || 'An unexpected error occurred'}
        </Alert>
      )}

      <Table
        responsive
        size="sm"
        className="border shadow-sm bg-white"
        style={{ border: '1px solid rgb(222 226 230)' }}
      >
        <thead>
          <tr>
            <FilterableHeader
              column={COLUMN_DEFINITIONS.full_account}
              title="Account Number"
              params={params}
              setParams={setParams}
              includeOrdering={false}
              popoverId="full_account"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
              style={{ minWidth: '250px' }}
            />
            <FilterableHeader
              column={COLUMN_DEFINITIONS.account_type}
              title="Type"
              params={params}
              setParams={setParams}
              includeOrdering={false}
              options={ACCOUNT_TYPE_OPTIONS}
              popoverId="account_type"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            <FilterableHeader
              column={COLUMN_DEFINITIONS.account_description}
              title="Description"
              params={params}
              setParams={setParams}
              includeOrdering={false}
              popoverId="account_description"
              activePopoverId={activePopoverId}
              setActivePopoverId={setActivePopoverId}
            />
            {activeSegments.map((segType) => (
              <FilterableHeader
                key={segType}
                column={getSegmentColumnDef(segType)}
                title={getSegmentName(segType)}
                params={params}
                setParams={setParams}
                includeOrdering={false}
                popoverId={segType}
                activePopoverId={activePopoverId}
                setActivePopoverId={setActivePopoverId}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {isSuccess &&
            accountsData.data.map((account) => (
              <tr key={`account-${account.id}`}>
                <td className="font-monospace">
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedAccountId(account.id)}
                    onKeyDown={(e) => e.key === 'Enter' && setSelectedAccountId(account.id)}
                    className="text-primary text-decoration-underline"
                    style={{ cursor: 'pointer' }}
                  >
                    {account.full_account}
                  </span>
                </td>
                <td>{account.account_type_name}</td>
                <td className="text-muted">{account.account_description}</td>
                {activeSegments.map((segType) => (
                  <td key={segType} className="text-center font-monospace">
                    {account[segType]?.code || ''}
                  </td>
                ))}
              </tr>
            ))}
          {isLoading && <TableLoading columns={totalColumns} />}
        </tbody>
      </Table>

      {isSuccess && (
        <TableFooter
          meta={accountsData.meta}
          params={params}
          setParams={setParams}
        />
      )}

      <AccountDetailModal
        show={selectedAccountId !== null}
        onHide={() => setSelectedAccountId(null)}
        accountId={selectedAccountId}
      />
    </>
  );
};

export default AccountsList;
